import { useEffect, useState, useMemo, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import useDebouncedValue from "../lib/useDebouncedValue";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import BulkActionBar from "../components/admin/BulkActionBar";
import RejectDialog from "../components/admin/RejectDialog";
import { ShieldCheck, ShieldQuestion, Shield, ShieldOff, Search, Loader2, Clock, History, ChevronDown, ChevronUp, FileText, Edit3, X, Eye, MailCheck, MailWarning, LogIn } from "lucide-react";
import { toast } from "sonner";

const STATUS_ICONS = {
    approved: { className: "bg-green-100 text-green-800", Icon: ShieldCheck },
    pending: { className: "bg-amber-100 text-amber-800", Icon: Clock },
    rejected: { className: "bg-red-100 text-red-800", Icon: ShieldOff },
    none: { className: "bg-gray-200 text-gray-700", Icon: ShieldQuestion },
};

// Plattform-Gebühr (%) je Händler — wird bei Stripe-Connect-Zahlungen als
// application_fee einbehalten. Inline-Editor pro Zeile.
function DealerFeeEditor({ dealer }) {
    const [val, setVal] = useState(String(dealer.platform_fee_percent ?? 0));
    const [busy, setBusy] = useState(false);
    const save = async () => {
        const pct = parseFloat(String(val).replace(",", "."));
        if (Number.isNaN(pct) || pct < 0 || pct > 20) { toast.error("0–20 % erlaubt"); return; }
        setBusy(true);
        try {
            await api.patch(`/admin/dealers/${dealer.id}/platform-fee`, { percent: pct });
            toast.success(`Plattform-Gebühr: ${pct} %`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="mt-2 flex items-center gap-1" data-testid={`platform-fee-${dealer.id}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Gebühr</span>
            <Input
                value={val}
                onChange={(e) => setVal(e.target.value)}
                className="h-7 w-14 text-xs font-mono-num rounded-sm px-1.5 text-right"
                inputMode="decimal"
                aria-label="Plattform-Gebühr in Prozent"
                data-testid={`platform-fee-input-${dealer.id}`}
            />
            <span className="text-[10px] text-gray-500">%</span>
            <Button type="button" size="sm" variant="outline" onClick={save} disabled={busy}
                className="h-7 rounded-sm text-[10px] px-2" data-testid={`platform-fee-save-${dealer.id}`}>
                OK
            </Button>
        </div>
    );
}

export default function AdminDealersPage() {
    const { t, i18n } = useTranslation();
    const { user, loading, impersonate } = useAuth();
    const navigate = useNavigate();
    const doImpersonate = async (id) => {
        try {
            await impersonate(id);
            toast.success("In Konto gewechselt");
            navigate("/dashboard");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Wechsel fehlgeschlagen");
        }
    };
    const [dealers, setDealers] = useState([]);
    // Jul-2026 (User-Wunsch) — Test-Konten-Bereinigung in die Systempflege
    // (/admin/vehicle-duplicates) verschoben.
    const [q, setQ] = useState(() => {
        if (typeof window === "undefined") return "";
        return new URLSearchParams(window.location.search).get("q") || "";
    });
    const [busyId, setBusyId] = useState(null);
    // Initial filter is read from ?status= so the header pill can deep-link
    // straight to the pending bucket.
    const initialFilter = useMemo(() => {
        if (typeof window === "undefined") return "all";
        const p = new URLSearchParams(window.location.search).get("status");
        return ["pending", "approved", "rejected", "none"].includes(p) ? p : "all";
    }, []);
    const [filter, setFilter] = useState(initialFilter);
    const [searching, setSearching] = useState(false);
    const [rejectFor, setRejectFor] = useState(null);
    const [rejectNote, setRejectNote] = useState("");
    const [selected, setSelected] = useState(() => new Set());
    const [bulkRunning, setBulkRunning] = useState(false);
    const [bulkExporting, setBulkExporting] = useState(false);
    const [bulkExportingListings, setBulkExportingListings] = useState(false);
    const [expandedDiff, setExpandedDiff] = useState(() => new Set());
    const [editProfileFor, setEditProfileFor] = useState(null);
    // Jun-2026 (User-Wunsch) — 1-Klick-Schnellprüfung im „Neueingang".
    const [quickCheckId, setQuickCheckId] = useState(null);
    const [quickCheckData, setQuickCheckData] = useState({});
    const [quickCheckLoading, setQuickCheckLoading] = useState(false);
    const debouncedQ = useDebouncedValue(q, 300);
    const dateLocale = i18n.resolvedLanguage === "en" ? "en-GB" : "de-DE";

    const openQuickCheck = async (id) => {
        if (quickCheckId === id) { setQuickCheckId(null); return; }
        setQuickCheckId(id);
        if (!quickCheckData[id]) {
            setQuickCheckLoading(true);
            try {
                const { data } = await api.get(`/admin/dealers/${id}/quickcheck`);
                setQuickCheckData((prev) => ({ ...prev, [id]: data }));
            } catch {
                toast.error("Schnellprüfung konnte nicht geladen werden");
                setQuickCheckId(null);
            } finally {
                setQuickCheckLoading(false);
            }
        }
    };

    const toggleDiff = (id) => {
        setExpandedDiff((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const load = async (query = "", statusFilter = "all") => {
        setSearching(true);
        try {
            const params = new URLSearchParams();
            if (query) params.set("q", query);
            if (statusFilter !== "all") params.set("status", statusFilter);
            const url = `/admin/dealers${params.toString() ? `?${params}` : ""}`;
            const { data } = await api.get(url);
            setDealers(data);
        } finally {
            setSearching(false);
        }
    };

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") return;
        load(debouncedQ, filter);
        // Clear stale selections whenever the visible list changes.
        setSelected(new Set());
    }, [user, loading, debouncedQ, filter]);

    if (loading) return <div className="p-12" data-testid="admin-dealers-loading">{t("admin_dealers.loading")}</div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="admin-dealers-forbidden">
                <h1 className="font-display text-3xl font-black mb-2">{t("admin_dealers.forbidden_title")}</h1>
                <p className="text-gray-600">{t("admin_dealers.forbidden_sub")}</p>
                <Link to="/" className="inline-block mt-6 text-sm font-bold underline">{t("admin_dealers.forbidden_home")}</Link>
            </div>
        );
    }

    const onApprove = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/admin/dealers/${id}/approve`, { note: "" });
            setDealers((prev) => prev.map((d) => (d.id === id ? { ...d, verification_status: "approved", verified: true } : d)));
            toast.success(t("admin_dealers.toast_verified"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_dealers.toast_action_failed"));
        } finally {
            setBusyId(null);
        }
    };

    const submitReject = async () => {
        if (!rejectFor) return;
        if (!rejectNote.trim()) {
            toast.error(t("admin_dealers.toast_note_required"));
            return;
        }
        setBusyId(rejectFor);
        try {
            await api.post(`/admin/dealers/${rejectFor}/reject`, { note: rejectNote.trim() });
            setDealers((prev) => prev.map((d) => (d.id === rejectFor
                ? { ...d, verification_status: "rejected", verified: false, verification_note: rejectNote.trim() }
                : d)));
            toast.success(t("admin_dealers.toast_rejected"));
            setRejectFor(null);
            setRejectNote("");
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_dealers.toast_action_failed"));
        } finally {
            setBusyId(null);
        }
    };

    const onResetToNone = async (id) => {
        if (!window.confirm(t("admin_dealers.confirm_reset"))) return;
        setBusyId(id);
        try {
            await api.post(`/admin/dealers/${id}/unverify`);
            setDealers((prev) => prev.map((d) => (d.id === id
                ? { ...d, verification_status: "none", verified: false }
                : d)));
            toast.success(t("admin_dealers.toast_reset"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_dealers.toast_action_failed"));
        } finally {
            setBusyId(null);
        }
    };

    const counts = dealers.reduce((acc, d) => {
        const s = d.verification_status || "none";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
    }, {});

    // All dealers are selectable so admins can also bulk-export ZIPs (audit /
    // listing reports) for already-approved accounts. The bulk-approve action
    // ignores approved IDs internally so re-clicking it on a verified dealer
    // is a no-op.
    const selectableIds = dealers.map((d) => d.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
    const someSelected = selected.size > 0 && !allSelected;

    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (allSelected) setSelected(new Set());
        else setSelected(new Set(selectableIds));
    };

    const bulkApprove = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(t("admin_dealers.confirm_bulk", { count: ids.length }))) return;
        setBulkRunning(true);
        try {
            const { data } = await api.post("/admin/dealers/bulk-approve", { ids });
            setDealers((prev) => prev.map((d) => (data.approved.includes(d.id)
                ? { ...d, verification_status: "approved", verified: true }
                : d)));
            setSelected(new Set());
            toast.success(data.skipped_count
                ? t("admin_dealers.toast_bulk_done_with_skips", { count: data.approved_count, skipped: data.skipped_count })
                : t("admin_dealers.toast_bulk_done", { count: data.approved_count }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_dealers.toast_bulk_failed"));
        } finally {
            setBulkRunning(false);
        }
    };

    const bulkExportZip = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        setBulkExporting(true);
        try {
            const r = await api.post("/admin/dealers/audit/bulk-export",
                { dealer_ids: ids, format: "pdf" },
                { responseType: "blob" },
            );
            const cd = r.headers["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(cd);
            const filename = m ? m[1] : "twowheels-audit-bulk.zip";
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const exported = Number(r.headers["x-exported-count"] || ids.length);
            const skipped = Number(r.headers["x-skipped-count"] || 0);
            toast.success(skipped
                ? t("admin_dealers.toast_export_done_with_skips", { count: exported, skipped })
                : t("admin_dealers.toast_export_done", { count: exported }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_dealers.toast_export_failed"));
        } finally {
            setBulkExporting(false);
        }
    };

    const bulkExportListingsZip = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        setBulkExportingListings(true);
        try {
            const r = await api.post("/admin/listings/reports/bulk-export",
                { dealer_ids: ids, format: "pdf" },
                { responseType: "blob" },
            );
            const cd = r.headers["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(cd);
            const filename = m ? m[1] : "twowheels-listings-bulk.zip";
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const exported = Number(r.headers["x-exported-count"] || ids.length);
            const skipped = Number(r.headers["x-skipped-count"] || 0);
            const listings = Number(r.headers["x-listing-count"] || 0);
            toast.success(skipped
                ? t("admin_dealers.toast_listing_export_done_with_skips", { count: exported, listings, skipped })
                : t("admin_dealers.toast_listing_export_done", { count: exported, listings }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_dealers.toast_export_failed"));
        } finally {
            setBulkExportingListings(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-dealers-page">
            <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-2">{t("admin_dealers.kicker")}</div>
                <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight">{t("admin_dealers.page_title")}</h1>
                <p className="text-gray-600 mt-2">
                    {t("admin_dealers.subtitle_summary", {
                        total: dealers.length,
                        pending: counts.pending || 0,
                        approved: counts.approved || 0,
                        rejected: counts.rejected || 0,
                    })}
                </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t("admin_dealers.search_placeholder")}
                        className="pl-10 pr-10 h-11 rounded-sm"
                        data-testid="admin-dealers-search"
                    />
                    {searching && (
                        <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" data-testid="admin-dealers-search-spinner" />
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    {[
                        { v: "all", labelKey: "filter_all" },
                        { v: "pending", labelKey: "filter_pending" },
                        { v: "approved", labelKey: "filter_approved" },
                        { v: "rejected", labelKey: "filter_rejected" },
                        { v: "none", labelKey: "filter_none" },
                    ].map((opt) => (
                        <Button
                            key={opt.v}
                            type="button"
                            onClick={() => setFilter(opt.v)}
                            variant={filter === opt.v ? "default" : "outline"}
                            className={`h-11 rounded-sm font-bold text-xs uppercase tracking-wider ${filter === opt.v ? "bg-[#111827] text-white" : ""}`}
                            data-testid={`filter-${opt.v}`}
                        >
                            {t(`admin_dealers.${opt.labelKey}`)}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-3 py-3 w-10">
                                <Checkbox
                                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                                    onCheckedChange={toggleAll}
                                    disabled={selectableIds.length === 0}
                                    aria-label={t("admin_dealers.select_all_aria")}
                                    data-testid="select-all-checkbox"
                                />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-500">{t("admin_dealers.col_dealer")}</th>
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-500 hidden md:table-cell">{t("admin_dealers.col_email")}</th>
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-500 hidden lg:table-cell">{t("admin_dealers.col_city")}</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-500 hidden md:table-cell">{t("admin_dealers.col_listings")}</th>
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-500">{t("admin_dealers.col_status")}</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-500">{t("admin_dealers.col_action")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dealers.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500" data-testid="admin-dealers-empty">{t("admin_dealers.empty")}</td></tr>
                        ) : (
                            dealers.map((d) => {
                                const status = d.verification_status || "none";
                                const meta = STATUS_ICONS[status];
                                const Icon = meta.Icon;
                                return (
                                    <Fragment key={d.id}>
                                    <tr className={`border-b border-gray-100 hover:bg-gray-50 align-top ${selected.has(d.id) ? "bg-yellow-50" : ""}`} data-testid={`admin-dealer-row-${d.id}`}>
                                        <td className="px-3 py-3">
                                            <Checkbox
                                                checked={selected.has(d.id)}
                                                onCheckedChange={() => toggleOne(d.id)}
                                                aria-label={t("admin_dealers.select_one_aria", { name: d.company_name || d.name })}
                                                data-testid={`select-${d.id}`}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-bold text-[#111827]">{d.company_name || d.name}</div>
                                            {d.name && d.company_name && <div className="text-xs text-gray-500">{d.name}</div>}
                                            <Link to={`/dealer/${d.id}`} className="text-xs text-blue-600 hover:underline" data-testid={`view-dealer-${d.id}`}>{t("admin_dealers.view_profile_link")}</Link>
                                            {d.business_doc_url && (
                                                <a href={d.business_doc_url} target="_blank" rel="noreferrer" className="block text-xs text-blue-600 hover:underline mt-0.5" data-testid={`view-doc-${d.id}`}>
                                                    {t("admin_dealers.view_doc_link")}
                                                </a>
                                            )}
                                            <Link to={`/admin/dealers/${d.id}/audit`} className="block text-xs text-blue-600 hover:underline mt-0.5" data-testid={`view-audit-${d.id}`}>
                                                {t("admin_dealers.view_audit_link")}
                                            </Link>
                                            {Array.isArray(d.business_doc_history) && d.business_doc_history.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleDiff(d.id)}
                                                    className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] underline decoration-dotted"
                                                    data-testid={`toggle-diff-${d.id}`}
                                                    aria-expanded={expandedDiff.has(d.id)}
                                                    aria-controls={`diff-panel-${d.id}`}
                                                >
                                                    <History className="w-3 h-3" />
                                                    {t("admin_dealers.versions_label", { count: d.business_doc_history.length + 1 })}
                                                    {expandedDiff.has(d.id)
                                                        ? <ChevronUp className="w-3 h-3" />
                                                        : <ChevronDown className="w-3 h-3" />}
                                                </button>
                                            )}
                                            {expandedDiff.has(d.id) && Array.isArray(d.business_doc_history) && d.business_doc_history.length > 0 && (
                                                <div
                                                    id={`diff-panel-${d.id}`}
                                                    className="mt-2 border border-gray-200 rounded-sm bg-gray-50 p-2 space-y-1.5 text-[11px] max-w-[320px]"
                                                    data-testid={`diff-panel-${d.id}`}
                                                >
                                                    <div className="flex items-center gap-2 px-1 py-1 bg-green-50 border-l-2 border-green-500 rounded-sm">
                                                        <Badge className="bg-green-600 hover:bg-green-600 text-white rounded-sm text-[9px] px-1.5 py-0 shrink-0">{t("admin_dealers.version_current")}</Badge>
                                                        <a
                                                            href={d.business_doc_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-blue-600 hover:underline truncate font-mono-num flex items-center gap-1 min-w-0"
                                                            data-testid={`diff-current-${d.id}`}
                                                            title={d.business_doc_url}
                                                        >
                                                            <FileText className="w-3 h-3 shrink-0" />
                                                            <span className="truncate">{d.business_doc_url.split("/").pop() || d.business_doc_url}</span>
                                                        </a>
                                                    </div>
                                                    {[...d.business_doc_history].reverse().map((h, idx) => {
                                                        const vNum = d.business_doc_history.length - idx;
                                                        return (
                                                            <div key={`${h.url}-${idx}`} className="flex items-center gap-2 px-1 py-1 border-l-2 border-gray-300">
                                                                <Badge variant="outline" className="rounded-sm text-[9px] px-1.5 py-0 shrink-0 text-gray-600 border-gray-300">
                                                                    {t("admin_dealers.version_short", { n: vNum })}
                                                                </Badge>
                                                                <div className="min-w-0 flex-1">
                                                                    <a
                                                                        href={h.url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="text-blue-600 hover:underline truncate font-mono-num flex items-center gap-1"
                                                                        data-testid={`diff-version-${d.id}-${vNum}`}
                                                                        title={h.url}
                                                                    >
                                                                        <FileText className="w-3 h-3 shrink-0" />
                                                                        <span className="truncate">{h.url.split("/").pop() || h.url}</span>
                                                                    </a>
                                                                    {h.archived_at && (
                                                                        <div className="text-gray-500 text-[10px]">
                                                                            {new Date(h.archived_at).toLocaleString(dateLocale, { dateStyle: "short", timeStyle: "short" })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 hidden md:table-cell font-mono-num">{d.email}</td>
                                        <td className="px-4 py-3 text-gray-700 hidden lg:table-cell">{d.city || "—"}</td>
                                        <td className="px-4 py-3 text-right hidden md:table-cell font-mono-num font-bold">{d.listings_count}</td>
                                        <td className="px-4 py-3">
                                            <Badge className={`${meta.className} hover:${meta.className} gap-1 rounded-sm`} data-testid={`status-${d.id}`}>
                                                <Icon className="w-3 h-3" /> {t(`admin_dealers.status_${status}`)}
                                            </Badge>
                                            {status === "rejected" && d.verification_note && (
                                                <div className="text-[11px] text-red-700 mt-1 max-w-[200px]" data-testid={`note-${d.id}`}>{d.verification_note}</div>
                                            )}
                                            <DealerFeeEditor dealer={d} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex gap-1 justify-end flex-wrap">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openQuickCheck(d.id)}
                                                    className={`rounded-sm text-xs font-bold uppercase tracking-wider ${quickCheckId === d.id ? "bg-[#FFCC00]/30 border-[#FFCC00]" : ""}`}
                                                    data-testid={`quickcheck-toggle-${d.id}`}
                                                    aria-expanded={quickCheckId === d.id}
                                                >
                                                    <Eye className="w-3 h-3 mr-1" /> Schnellprüfung
                                                </Button>
                                                {status !== "approved" && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        disabled={busyId === d.id}
                                                        onClick={() => onApprove(d.id)}
                                                        className="bg-[#111827] hover:bg-black text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                                                        data-testid={`approve-${d.id}`}
                                                    >
                                                        <ShieldCheck className="w-3 h-3 mr-1" /> {t("admin_dealers.btn_approve")}
                                                    </Button>
                                                )}
                                                {(status === "pending" || status === "approved") && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={busyId === d.id}
                                                        onClick={() => { setRejectFor(d.id); setRejectNote(""); }}
                                                        className="rounded-sm text-xs font-bold uppercase tracking-wider border-red-300 text-red-700 hover:bg-red-50"
                                                        data-testid={`reject-${d.id}`}
                                                    >
                                                        <ShieldOff className="w-3 h-3 mr-1" /> {t("admin_dealers.btn_reject")}
                                                    </Button>
                                                )}
                                                {(status === "approved" || status === "rejected") && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={busyId === d.id}
                                                        onClick={() => onResetToNone(d.id)}
                                                        className="rounded-sm text-xs font-bold uppercase tracking-wider"
                                                        data-testid={`reset-${d.id}`}
                                                    >
                                                        <Shield className="w-3 h-3 mr-1" /> {t("admin_dealers.btn_reset")}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditProfileFor(d)}
                                                    className="rounded-sm text-xs font-bold uppercase tracking-wider border-[#FFCC00] text-[#111827] hover:bg-[#FFCC00]/20"
                                                    data-testid={`edit-profile-${d.id}`}
                                                >
                                                    <Edit3 className="w-3 h-3 mr-1" /> Profil bearbeiten
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    onClick={() => doImpersonate(d.id)}
                                                    className="rounded-sm text-xs font-bold uppercase tracking-wider bg-[#B45309] hover:bg-[#92400E] text-white"
                                                    data-testid={`impersonate-${d.id}`}
                                                    title="Als dieser Nutzer anmelden (Admin-Modus)"
                                                >
                                                    <LogIn className="w-3 h-3 mr-1" /> In Konto wechseln
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    {quickCheckId === d.id && (
                                        <tr className="bg-[#FFFBEB] border-b border-gray-200" data-testid={`quickcheck-panel-${d.id}`}>
                                            <td colSpan={7} className="px-4 py-4">
                                                {quickCheckLoading && !quickCheckData[d.id] ? (
                                                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lädt Stammdaten …</div>
                                                ) : quickCheckData[d.id] ? (
                                                    <QuickCheckPanel
                                                        data={quickCheckData[d.id]}
                                                        busy={busyId === d.id}
                                                        onApprove={() => onApprove(d.id)}
                                                        onReject={() => { setRejectFor(d.id); setRejectNote(""); }}
                                                        dateLocale={dateLocale}
                                                    />
                                                ) : null}
                                            </td>
                                        </tr>
                                    )}
                                    </Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <BulkActionBar
                count={selected.size}
                busy={bulkRunning}
                exporting={bulkExporting}
                exportingListings={bulkExportingListings}
                onClear={() => setSelected(new Set())}
                onApprove={bulkApprove}
                onExportZip={bulkExportZip}
                onExportListingsZip={bulkExportListingsZip}
            />

            <RejectDialog
                open={!!rejectFor}
                note={rejectNote}
                onNoteChange={setRejectNote}
                onCancel={() => { setRejectFor(null); setRejectNote(""); }}
                onSubmit={submitReject}
                busy={busyId === rejectFor}
            />

            <EditDealerProfileDialog
                dealer={editProfileFor}
                onClose={() => setEditProfileFor(null)}
                onSaved={(updated) => {
                    setDealers((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
                    setEditProfileFor(null);
                }}
            />
        </div>
    );
}

// =============================================================================
// Admin – Dealer Profile Editor (Inline-Modal)
// Phase 3.60 — Komplett-Überarbeitung: korrekte Backend-Keys + Sektionen.
// Vorher waren `ustid`, `iban`, `website`, `phone`, `handelsregister` nur
// optisch im Modal — Backend hat sie still gedroppt (Pydantic ignoriert
// unbekannte Felder). Jetzt 1:1 gemappt zum DealerProfile-Modell.
// =============================================================================

const PROFILE_SECTIONS = [
    {
        id: "stammdaten",
        title: "Stammdaten",
        fields: [
            { key: "company_name", label: "Firmenname", placeholder: "Beispiel GmbH" },
            { key: "name", label: "Inhaber / Ansprechpartner", placeholder: "Max Mustermann" },
            { key: "email", label: "E-Mail (Login)", placeholder: "info@firma.de", readonly: true },
            { key: "customer_number", label: "Kundennummer (nur Admin)", placeholder: "kd-12345678" },
            { key: "dealer_alias_email", label: "System-Posteingang (nur Admin)", placeholder: "kd-12345678@twowheels.info" },
            { key: "phone_office", label: "Festnetz", placeholder: "+49 30 …" },
            { key: "phone_mobile", label: "Mobil", placeholder: "+49 170 …" },
            { key: "contact_email", label: "Kontakt-E-Mail (öffentlich)", placeholder: "kontakt@firma.de" },
            { key: "homepage_url", label: "Webseite", placeholder: "https://…" },
        ],
    },
    {
        id: "anschrift",
        title: "Anschrift",
        fields: [
            { key: "street", label: "Straße + Nr.", placeholder: "Musterstraße 1" },
            { key: "postal_code", label: "PLZ", placeholder: "10115" },
            { key: "city", label: "Stadt", placeholder: "Berlin" },
            { key: "country", label: "Land", placeholder: "Deutschland" },
        ],
    },
    {
        id: "steuer",
        title: "Steuer & Register",
        fields: [
            { key: "tax_id", label: "Steuer-Nr.", placeholder: "12/345/67890" },
            { key: "vat_id", label: "USt-ID", placeholder: "DE000000000" },
            { key: "handelsregister", label: "Handelsregister", placeholder: "HRB 12345 Berlin" },
        ],
    },
    {
        id: "bank",
        title: "Bankverbindung",
        fields: [
            { key: "bank_account_holder", label: "Kontoinhaber", placeholder: "Beispiel GmbH" },
            { key: "bank_iban", label: "IBAN", placeholder: "DE00 …" },
            { key: "bank_bic", label: "BIC", placeholder: "MARKDEF1100" },
            { key: "bank_name", label: "Bank", placeholder: "Sparkasse Berlin" },
        ],
    },
];

const PROFILE_TOGGLES = [
    {
        id: "haendlertyp",
        title: "Händlertyp & Programme",
        toggles: [
            { key: "dealer_free", label: "Freier Händler" },
            { key: "dealer_authorized", label: "Vertragshändler" },
            { key: "dealer_workshop", label: "Werkstatt" },
            { key: "dealer_parts", label: "Teile & Zubehör" },
            { key: "dealer_motorcycles", label: "Motorräder" },
            { key: "dealer_bicycles", label: "Fahrräder" },
        ],
    },
    {
        id: "marktplatz",
        title: "Marktplatzeinstellungen",
        toggles: [
            { key: "allow_offers", label: "Preisangebote zulassen" },
            { key: "allow_tradein", label: "Inzahlungnahme zulassen" },
            { key: "allow_chat", label: "Chat zulassen" },
            { key: "allow_auctions", label: "Auktionen anbieten" },
            { key: "accept_buy_requests", label: "Privatkunden-Ankäufe" },
            { key: "program_sharing", label: "Sharing / Vermietung" },
            { key: "program_pfand", label: "Pfand-/Refinanzierung" },
            { key: "social_media_enabled", label: "Sozialmedia-Veröffentlichung" },
            { key: "marketing_banners_enabled", label: "Marketing-Banner" },
            { key: "embed_enabled", label: "Whitelabel-Embed" },
            { key: "stats_email_monthly", label: "Monatlicher Stats-PDF" },
        ],
    },
];

// Alle einzelnen Field-Keys flach zusammenfassen (für initial form load + diff).
const ALL_PROFILE_FIELD_KEYS = PROFILE_SECTIONS.flatMap((s) => s.fields.map((f) => f.key));
const ALL_PROFILE_TOGGLE_KEYS = PROFILE_TOGGLES.flatMap((s) => s.toggles.map((t) => t.key));

// =============================================================================
// Jun-2026 (User-Wunsch) — 1-Klick-Schnellprüfung im „Neueingang"
// Stammdaten + Gewerbenachweis-Vorschau + direkte Freigabe/Ablehnung.
// =============================================================================
function QuickCheckPanel({ data, busy, onApprove, onReject, dateLocale }) {
    const isImg = data.business_doc_url && /\.(png|jpe?g|webp|gif)$/i.test(data.business_doc_url);
    const Row = ({ label, value, mono }) => (
        <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`text-sm text-[#111827] truncate ${mono ? "font-mono-num" : ""}`}>{value || <span className="text-gray-300">—</span>}</div>
        </div>
    );
    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-5" data-testid="quickcheck-content">
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-xs font-black uppercase tracking-wider text-[#111827]">Stammdaten-Schnellprüfung</h4>
                    {data.email_verified
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-sm" data-testid="quickcheck-email-verified"><MailCheck className="w-3 h-3" /> E-Mail bestätigt</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-sm" data-testid="quickcheck-email-unverified"><MailWarning className="w-3 h-3" /> E-Mail unbestätigt</span>}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Row label="Firma" value={data.company_name} />
                    <Row label="Ansprechpartner" value={data.contact_name} />
                    <Row label="E-Mail" value={data.email} mono />
                    <Row label="Handynummer" value={data.phone} mono />
                    <Row label="Festnetz" value={data.phone_office} mono />
                    <Row label="Standort-Code" value={data.site_code} mono />
                    <Row label="Straße" value={data.street} />
                    <Row label="PLZ" value={data.postal_code} mono />
                    <Row label="Stadt" value={data.city} />
                    <Row label="USt-IdNr." value={data.vat_id} mono />
                    <Row label="Steuer-Nr." value={data.tax_id} mono />
                    <Row label="Handelsregister" value={data.handelsregister} />
                    <Row label="IBAN" value={data.bank_iban} mono />
                    <Row label="Bank" value={data.bank_name} />
                    <Row label="Registriert am" value={data.created_at ? new Date(data.created_at).toLocaleDateString(dateLocale, { dateStyle: "medium" }) : null} />
                </div>
                <div className="flex gap-2 mt-4">
                    <Button type="button" size="sm" disabled={busy} onClick={onApprove}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                        data-testid="quickcheck-approve">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />} Freigeben
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onReject}
                        className="rounded-sm text-xs font-bold uppercase tracking-wider border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="quickcheck-reject">
                        <ShieldOff className="w-3 h-3 mr-1" /> Ablehnen
                    </Button>
                </div>
            </div>
            <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Gewerbenachweis</div>
                {data.business_doc_url ? (
                    <div className="border border-gray-200 rounded-sm bg-white p-2" data-testid="quickcheck-doc">
                        {isImg ? (
                            <a href={data.business_doc_url} target="_blank" rel="noreferrer">
                                <img src={data.business_doc_url} alt="Gewerbenachweis" className="w-full max-h-48 object-contain rounded-sm" />
                            </a>
                        ) : (
                            <a href={data.business_doc_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline py-3" data-testid="quickcheck-doc-link">
                                <FileText className="w-5 h-5" /> Nachweis öffnen (PDF)
                            </a>
                        )}
                    </div>
                ) : (
                    <div className="border border-dashed border-gray-300 rounded-sm bg-white px-3 py-6 text-center text-xs text-gray-400" data-testid="quickcheck-doc-missing">
                        Noch kein Gewerbenachweis hochgeladen.
                    </div>
                )}
            </div>
        </div>
    );
}

function EditDealerProfileDialog({ dealer, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!dealer) return;
        const initial = {};
        for (const k of ALL_PROFILE_FIELD_KEYS) initial[k] = dealer[k] ?? "";
        for (const k of ALL_PROFILE_TOGGLE_KEYS) initial[k] = !!dealer[k];
        setForm(initial);
    }, [dealer]);

    if (!dealer) return null;

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {};
            // Text-Felder: nur diffen (leerer String → null)
            for (const sec of PROFILE_SECTIONS) {
                for (const f of sec.fields) {
                    if (f.readonly) continue;
                    const newVal = (form[f.key] || "").trim();
                    const oldVal = (dealer[f.key] || "").toString().trim();
                    if (newVal !== oldVal) payload[f.key] = newVal || null;
                }
            }
            // Toggle-Felder: nur diffen
            for (const grp of PROFILE_TOGGLES) {
                for (const t of grp.toggles) {
                    if (!!form[t.key] !== !!dealer[t.key]) payload[t.key] = !!form[t.key];
                }
            }
            if (Object.keys(payload).length === 0) {
                toast.info("Keine Änderungen");
                onClose();
                return;
            }
            const { data } = await api.patch(`/admin/dealers/${dealer.id}/profile`, payload);
            toast.success(`Profil aktualisiert (${Object.keys(payload).length} Feld${Object.keys(payload).length === 1 ? "" : "er"})`);
            onSaved && onSaved(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="edit-dealer-profile-modal">
            <div className="bg-white border-l-4 border-[#FFCC00] rounded-sm w-full max-w-4xl max-h-[92vh] flex flex-col">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-bold text-xl">Händler-Stammdaten</h3>
                        <p className="text-[11px] text-gray-500">Admin-Edit für {dealer.company_name || dealer.name} · {dealer.email}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-[#111827]" data-testid="edit-profile-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-5 py-4 overflow-y-auto space-y-6">
                    {PROFILE_SECTIONS.map((sec) => (
                        <section key={sec.id} data-testid={`profile-section-${sec.id}`}>
                            <h4 className="text-xs font-black uppercase tracking-wider text-[#111827] mb-2 pb-1 border-b border-gray-200">{sec.title}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {sec.fields.map((f) => (
                                    <div key={f.key}>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{f.label}</label>
                                        <Input
                                            value={form[f.key] || ""}
                                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                            placeholder={f.placeholder}
                                            disabled={f.readonly}
                                            className="rounded-sm h-9"
                                            data-testid={`edit-field-${f.key}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                    {PROFILE_TOGGLES.map((grp) => (
                        <section key={grp.id} data-testid={`profile-toggle-section-${grp.id}`}>
                            <h4 className="text-xs font-black uppercase tracking-wider text-[#111827] mb-2 pb-1 border-b border-gray-200">{grp.title}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {grp.toggles.map((t) => (
                                    <label key={t.key} className="flex items-center gap-2 cursor-pointer text-sm bg-gray-50 border border-gray-200 rounded-sm px-3 py-2" data-testid={`edit-toggle-row-${t.key}`}>
                                        <input
                                            type="checkbox"
                                            checked={!!form[t.key]}
                                            onChange={(e) => setForm({ ...form, [t.key]: e.target.checked })}
                                            className="w-4 h-4 accent-[#111827]"
                                            data-testid={`edit-toggle-${t.key}`}
                                        />
                                        <span className="font-medium text-[#111827]">{t.label}</span>
                                    </label>
                                ))}
                            </div>
                        </section>
                    ))}
                    <p className="text-[11px] text-gray-500 pt-2 border-t border-gray-100">
                        ⓘ Weitere Felder (Benachrichtigungs-Routing, Werkstatt-Slots, Social-Media-Logins, Marketing-Texte, Whitelabel-Konfiguration) kann der Händler selbst in seinen Einstellungen pflegen. Hier liegt der Fokus auf Stammdaten + Programm-Aktivierung.
                    </p>
                </div>
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm font-bold uppercase tracking-wider text-xs" data-testid="edit-profile-cancel">
                        Abbrechen
                    </Button>
                    <Button onClick={submit} disabled={saving} className="bg-[#111827] text-white hover:bg-[#FFCC00] hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs" data-testid="edit-profile-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        Speichern
                    </Button>
                </div>
            </div>
        </div>
    );
}
